import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, ChevronRight } from 'lucide-react'
import { getDirectoryNeighbourhoods } from '../../lib/api'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { NavCardGridSkeleton } from '../../components/ui/Skeletons'
import { DIRECTORY_PAGE_MAX, slugToLabel } from './directoryUtils'

export function DirectoryAreas() {
  const { countrySlug, countySlug } = useParams<{ countrySlug: string; countySlug: string }>()
  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()

  const { data: areas, isLoading } = useQuery({
    queryKey: ['directory', 'areas', countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryNeighbourhoods(countrySlug!, countySlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!(countrySlug && countySlug),
  })

  const countryLabel = slugToLabel(countrySlug)
  const countyLabel = slugToLabel(countySlug)

  return (
    <PageWrapper>
      <Seo
        title={`${label} by Area in ${countyLabel} | FixDelivered`}
        description={`Browse ${label.toLowerCase()} by named area in ${countyLabel}. Find local plumbers in Putney, Clapham, Brixton and more.`}
        path={`/plumbers/${countrySlug}/${countySlug}/areas`}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
          { name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` },
          { name: 'Areas', url: `${basePath}/${countrySlug}/${countySlug}/areas` },
        ]}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <Breadcrumb items={[
          { label: countryLabel, href: `${basePath}/${countrySlug}` },
          { label: countyLabel, href: `${basePath}/${countrySlug}/${countySlug}` },
          { label: 'Areas' },
        ]} />

        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{label} by Area in {countyLabel}</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Browse plumbers by named neighbourhood or district.
          </p>
        </div>

        {isLoading ? (
          <NavCardGridSkeleton count={8} />
        ) : areas?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {areas.map(a => (
              <Link
                key={a.neighbourhoodSlug}
                to={`${basePath}/${countrySlug}/${countySlug}/areas/${a.neighbourhoodSlug}`}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{a.neighbourhood}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{a.count} plumber{a.count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400">
            <p>No named areas found for this county.</p>
            <Link to={`${basePath}/${countrySlug}/${countySlug}`} className="text-trust-500 hover:underline text-sm mt-2 inline-block">
              Browse towns in {countyLabel}
            </Link>
          </div>
        )}
      </div>
    </PageWrapper>
  )
}
